import React from "react";
import { motion } from "framer-motion";
import { CATEGORIES, fmt, fmtDelta } from "../../lib/game";

function deltaSummary(impact) {
  const parts = [];
  if (impact.cash) parts.push({ label: "Cash", v: impact.cash });
  if (impact.investments) parts.push({ label: "Investments", v: impact.investments });
  if (impact.debts) parts.push({ label: "Debt", v: impact.debts });
  return parts;
}

export const ScenarioCard = ({ card, flipped, onFlip, onChoose, disabled }) => {
  const meta = CATEGORIES[card.category];
  const Icon = meta.Icon;

  return (
    <div className="flip-scene w-full max-w-lg mx-auto" style={{ minHeight: 420 }}>
      <div
        className={`flip-inner ${flipped ? "is-flipped" : ""}`}
        style={{ minHeight: 420 }}
      >
        {/* FRONT */}
        <button
          type="button"
          onClick={onFlip}
          data-testid="card-flip-front"
          className={`flip-face flip-front w-full rounded-3xl border-2 ${meta.border} bg-gradient-to-b ${meta.gradient} bg-slate-900/60 shadow-2xl ${meta.glow} flex flex-col items-center justify-center gap-6 p-8 cursor-pointer transition-transform hover:-translate-y-1`}
          style={{ minHeight: 420 }}
        >
          <div
            className={`w-24 h-24 rounded-2xl flex items-center justify-center border ${meta.border} ${meta.bg}`}
          >
            <Icon className={`w-12 h-12 ${meta.text}`} strokeWidth={2} />
          </div>
          <div className="text-center">
            <span
              className={`inline-block text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border ${meta.badge}`}
            >
              {meta.label}
            </span>
            <h3 className="mt-4 text-2xl font-heading font-bold text-slate-100">
              A {meta.label} Card
            </h3>
            <p className="mt-2 text-sm text-slate-400">Tap to reveal your scenario</p>
          </div>
          <span className={`text-xs font-semibold uppercase tracking-[0.3em] ${meta.text} animate-pulse`}>
            Flip to reveal
          </span>
        </button>

        {/* BACK */}
        <div
          data-testid="card-flip-back"
          className={`flip-face flip-back w-full rounded-3xl border ${meta.border} bg-slate-900/90 shadow-2xl p-6 sm:p-7 flex flex-col`}
          style={{ minHeight: 420 }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${meta.bg} border ${meta.border}`}>
              <Icon className={`w-5 h-5 ${meta.text}`} />
            </div>
            <span className={`text-xs font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border ${meta.badge}`}>
              {meta.label}
            </span>
            {card.focus && (
              <span data-testid="card-focus-chip" className="ml-auto text-[10px] font-bold uppercase tracking-wider text-amber-200 bg-amber-500/15 border border-amber-400/40 rounded-full px-2 py-0.5">
                Practice area
              </span>
            )}
            <span className={`${card.focus ? "ml-2" : "ml-auto"} text-[11px] font-semibold text-slate-400 capitalize`}>
              {card.concept_tag}
            </span>
          </div>

          <p className="text-slate-200 leading-relaxed text-[15px] mb-5">
            {card.scenario_text}
          </p>

          <div className="space-y-3 mt-auto">
            {card.choices.map((choice, idx) => {
              const parts = deltaSummary(choice.impact);
              return (
                <motion.button
                  key={idx}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChoose(idx)}
                  data-testid={`card-option-${idx}`}
                  whileHover={{ scale: disabled ? 1 : 1.015 }}
                  whileTap={{ scale: disabled ? 1 : 0.985 }}
                  className={`w-full text-left rounded-xl border border-slate-700 bg-slate-800/60 hover:border-slate-500 hover:bg-slate-800 px-4 py-3 transition-colors disabled:opacity-60 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-100">{choice.text}</p>
                      {choice.note && (
                        <p className="text-xs text-slate-400 mt-0.5">{choice.note}</p>
                      )}
                    </div>
                  </div>
                  {parts.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {parts.map((p) => {
                        const good =
                          p.label === "Debt" ? p.v < 0 : p.v > 0;
                        return (
                          <span
                            key={p.label}
                            className={`text-[11px] font-mono font-semibold px-2 py-0.5 rounded-md border ${
                              good
                                ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
                                : "bg-red-500/15 text-red-300 border-red-500/30"
                            }`}
                          >
                            {fmtDelta(p.v)} {p.label}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
